/**
 * Parsing + Mixpanel mapping for the Keycloak `license_info`.
 *
 * `license_info` arrives on the userinfo profile as a JSON-encoded string of
 * `LicenseInfoEntry[]`. We flatten it into list-style Mixpanel user properties
 * (`license_types`, `license_groups`) so license type and group can be filtered
 * and segmented on directly in Mixpanel.
 */

export type LicenseGroupId = 1 | 2 | 3 | 4 | 99;

export type LicenseGroupName = 'standard' | 'EN15804' | 'v4' | 'sandbox' | 'pre-release';

// Known license types with a string fallback so unexpected values still type-check
// while the literals are still suggested by autocomplete.
export type LicenseType = 'perpetual' | 'subscription' | 'trial' | (string & Record<never, never>);

export type License = {
  id: number;
  type: LicenseType;
  expiry_date: string | null;
  maintenance_end_date: string | null;
  start_date: string | null;
  has_file_access: boolean;
  groups: number[];
};

export type LicenseInfoEntry = {
  license: License;
};

export type LicenseMixpanelProperties = {
  license_count: number;
  license_types: LicenseType[];
  license_groups: string[];
  license_group_ids: number[];
};

export const LICENSE_GROUP_NAMES: Record<LicenseGroupId, LicenseGroupName> = {
  1: 'standard',
  2: 'EN15804',
  3: 'v4',
  4: 'sandbox',
  99: 'pre-release',
};

export const getLicenseGroupName = (groupId: number): string =>
  LICENSE_GROUP_NAMES[groupId as LicenseGroupId] ?? `unknown (${groupId})`;

export const parseLicenseInfo = (rawLicenseInfo?: string | null): LicenseInfoEntry[] => {
  if (!rawLicenseInfo) return [];

  try {
    const parsed = JSON.parse(rawLicenseInfo);
    return Array.isArray(parsed) ? (parsed as LicenseInfoEntry[]) : [];
  } catch {
    return [];
  }
};

export const getLicenseMixpanelProperties = (rawLicenseInfo?: string | null): LicenseMixpanelProperties => {
  const licenses = parseLicenseInfo(rawLicenseInfo).map((entry) => entry.license);

  const licenseTypes = [...new Set(licenses.map((license) => license.type))].sort();
  const groupIds = [...new Set(licenses.flatMap((license) => license.groups ?? []))].sort((a, b) => a - b);

  return {
    license_count: licenses.length,
    license_types: licenseTypes,
    license_groups: groupIds.map(getLicenseGroupName),
    license_group_ids: groupIds,
  };
};
